import { useState } from 'react';
import { Button } from './UIComponents';
import { resolveApiBaseUrl } from '../api/baseUrl';
import { handleApiError } from '../api/client';
import { linkGoogleAccount } from '../api/oauth';
import { useTranslation } from 'react-i18next';

export type GoogleLoginMode = 'login' | 'register' | 'link';

export interface GoogleLoginButtonProps {
  mode?: GoogleLoginMode;
  redirectUrl?: string;
  className?: string;
}

function GoogleIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function GoogleLoginButton({ mode = 'login', redirectUrl, className = '' }: GoogleLoginButtonProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const label =
    mode === 'link'
      ? t('auth.linkGoogle', { defaultValue: 'Link Google account' })
      : mode === 'register'
        ? t('auth.signUpWithGoogle', { defaultValue: 'Sign up with Google' })
        : t('auth.continueWithGoogle', { defaultValue: 'Continue with Google' });

  const handleClick = async () => {
    if (isLoading) return;
    setError('');
    setIsLoading(true);

    try {
      const fallbackRedirectUrl = mode === 'link' ? '/settings' : '/dashboard';
      const safeRedirectUrl = redirectUrl && redirectUrl.startsWith('/') ? redirectUrl : fallbackRedirectUrl;

      if (mode === 'link') {
        const { auth_url } = await linkGoogleAccount(safeRedirectUrl || '/settings');
        window.location.href = auth_url;
        return;
      }

      const params = new URLSearchParams();
      if (safeRedirectUrl) {
        params.set('redirect_url', safeRedirectUrl);
      }

      const apiBaseUrl = resolveApiBaseUrl();
      window.location.href = `${apiBaseUrl}/auth/oauth/google?${params.toString()}`;
    } catch (err: unknown) {
      setError(handleApiError(err));
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-center bg-white dark:bg-zinc-900"
        onClick={handleClick}
        isLoading={isLoading}
      >
        <GoogleIcon className="h-5 w-5 me-2" />
        {label}
      </Button>

      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
